import React from 'react';
import { Modal, Stack, Group, Text, Table, Badge, Alert, Box, Paper } from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import type { ProductImportPreview } from '../../../../../services/products/products.service';
import { useImportProductsController } from './useImportProductsController';

// POS blue ($color-ui-info); Mantine can't read SCSS tokens, so the hex is referenced here.
const POS_BLUE = '#2563EB';
const POS_BLUE_DARK = '#1D4FD1';
const MUTED = '#6B7280';
const AMBER = '#D97706';
const RED = '#E03131';

const ACTION_LABEL = { create: 'חדש', update: 'עודכן', unchanged: 'ללא שינוי' } as const;
const ACTION_COLOR = { create: POS_BLUE, update: POS_BLUE_DARK, unchanged: 'gray' } as const;
const SHEET_LABEL = { products: 'מוצרים', groups: 'קבוצות', items: 'פריטים' } as const;
const MAX_BYTES = 5 * 1024 * 1024;

const StatCard: React.FC<{ label: string; value: number | string; color: string }> = ({ label, value, color }) => (
  <Paper withBorder radius="md" p="xs" style={{ minWidth: 96, textAlign: 'center', borderColor: color }}>
    <Text fw={700} fz="xl" style={{ color }}>
      {value}
    </Text>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
  </Paper>
);

export type ImportProductsModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  onImported: (result: ProductImportPreview) => void;
};

const ImportProductsModalComponent: React.FC<ImportProductsModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  onImported,
}) => {
  const { file, preview, isPreviewing, isCommitting, error, handleFile, confirm, reset } = useImportProductsController(
    tenantId,
    employeeSession,
    onImported,
  );

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      onContextMenu={(event) => event.preventDefault()}
      title="ייבוא מוצרים"
      size="xl"
      centered
      classNames={{ root: navyModal.root }}
      closeButtonProps={{ 'data-testid': 'import-products-close' } as Record<string, string>}
    >
      <Stack gap="md" dir="rtl">
        <Dropzone
          data-testid="import-products-dropzone"
          onDrop={(files) => void handleFile(files[0] ?? null)}
          accept={[MIME_TYPES.xlsx]}
          multiple={false}
          maxSize={MAX_BYTES}
          loading={isPreviewing}
        >
          <Box mih={80}>
            <Text ta="center" fw={600}>
              {file ? file.name : 'גרור קובץ .xlsx לכאן או לחץ לבחירה'}
            </Text>
            <Text ta="center" size="sm" c="dimmed">
              אותו פורמט של קובץ הייצוא
            </Text>
          </Box>
        </Dropzone>

        {error && <Alert color="red">{error}</Alert>}

        {preview && (
          <>
            <Group gap="xs" wrap="wrap">
              <StatCard label="מוצרים חדשים" value={preview.summary.productsNew} color={POS_BLUE} />
              <StatCard label="מוצרים עודכנו" value={preview.summary.productsUpdated} color={POS_BLUE_DARK} />
              <StatCard label="ללא שינוי" value={preview.summary.productsUnchanged} color={MUTED} />
              <StatCard label="קבוצות (חדש/עודכן)" value={`${preview.summary.groupsNew}/${preview.summary.groupsUpdated}`} color={POS_BLUE} />
              <StatCard label="פריטים (חדש/עודכן)" value={`${preview.summary.itemsNew}/${preview.summary.itemsUpdated}`} color={POS_BLUE} />
              {preview.summary.warnings > 0 && <StatCard label="אזהרות" value={preview.summary.warnings} color={AMBER} />}
              {preview.summary.errors > 0 && <StatCard label="שגיאות" value={preview.summary.errors} color={RED} />}
            </Group>

            {preview.issues.length > 0 && (
              <Stack gap={4}>
                {preview.issues.map((issue, index) => (
                  <Alert key={`${issue.sheet}-${issue.row}-${index}`} color={issue.severity === 'error' ? 'red' : 'yellow'} p="xs">
                    {SHEET_LABEL[issue.sheet]} שורה {issue.row}: {issue.message}
                  </Alert>
                ))}
              </Stack>
            )}

            {preview.products.length > 0 && (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>שם</Table.Th>
                    <Table.Th>פעולה</Table.Th>
                    <Table.Th>שדות שעודכנו</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {preview.products.map((p, index) => (
                    <Table.Tr key={`${p.name}#${index}`}>
                      <Table.Td>{p.name}</Table.Td>
                      <Table.Td>
                        <Badge color={ACTION_COLOR[p.action]}>{ACTION_LABEL[p.action]}</Badge>
                      </Table.Td>
                      <Table.Td>{p.changedFields.join(', ')}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </>
        )}

        <Group justify="flex-start">
          <Button
            variant="save"
            onClick={() => void confirm()}
            disabled={!preview?.canCommit || isPreviewing}
            loading={isCommitting}
            loadingLabel="מייבא..."
            dataTestId="import-products-confirm"
          >
            ייבוא
          </Button>
          <Button variant="cancel" onClick={close} disabled={isCommitting} dataTestId="import-products-cancel">
            ביטול
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export const ImportProductsModal = React.memo(ImportProductsModalComponent);
